import React, { useEffect, useState } from 'react';

import styled from 'styled-components/macro';

import { useTheme } from 'providers/ThemeProvider';
import { sizePalette } from 'utils/styleConstants';
import Icon from 'components/Input/Icon';

const StyledCheckbox = styled.input<React.ComponentProps<'input'>>`
  position: absolute;
  top: -${sizePalette.spacing[1.5]};
  left: -${sizePalette.spacing[1.5]};
  width: ${sizePalette.spacing[5]};
  height: ${sizePalette.spacing[5]};
  opacity: ${sizePalette.opacity[0]};
`;

const CheckContainer = styled.div<{
  isChecked: boolean;
  disabled: boolean;
  isEditable: boolean;
  color: string | undefined;
}>`
  transition: all ${sizePalette.transitionDuration[150]};
  cursor: ${({ isEditable }) => (isEditable ? 'pointer' : 'not-allowed')};
  min-width: ${sizePalette.spacing.pxToRem(18)};
  height: ${sizePalette.spacing.pxToRem(18)};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  background-color: ${({ disabled, theme: { colorPalette } }) =>
    disabled
      ? colorPalette.isDarkMode
        ? colorPalette.grey.grey8
        : colorPalette.grey.grey1
      : colorPalette.isDarkMode
      ? colorPalette.black
      : colorPalette.white};

  border: ${(props) =>
    props.isChecked
      ? `${sizePalette.borderWidth.DEFAULT} solid ${props.theme.colorPalette.turquoise.normal}`
      : `${sizePalette.borderWidth.DEFAULT} solid ${props.theme.colorPalette.grey.grey5}`};

  border: ${({ isChecked, disabled, theme: { colorPalette } }) =>
    isChecked && disabled && `${sizePalette.borderWidth.DEFAULT} solid ${colorPalette.grey.grey5}`};

  border-radius: ${sizePalette.borderRadius.DEFAULT};
  border: ${(props) => props.color && `${sizePalette.borderWidth.DEFAULT} solid ${props.color}`};

  &:focus-within {
    border-color: ${({ color, isEditable, theme }) =>
      !color && isEditable ? theme.colorPalette.turquoise.normal : theme.colorPalette.grey.grey5};
    outline: none;
  }

  ${({ isChecked, theme, color, disabled }) =>
    isChecked &&
    !disabled &&
    `border-color: ${!color ? theme.colorPalette.turquoise.normal : color};
     outline: none;`}

  > input {
    cursor: ${({ isEditable }) => (isEditable ? 'pointer' : 'not-allowed')};
  }
`;

const CheckWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: max-content;
`;

const CheckLabel = styled.div<{ hasLabel: boolean; isEditable: boolean }>`
  opacity: ${({ isEditable, theme }) =>
    theme.colorPalette.isDarkMode
      ? sizePalette.opacity[100]
      : isEditable
      ? sizePalette.opacity[100]
      : sizePalette.opacity[40]};
  display: ${(props) => (props.hasLabel ? 'block' : 'none')};
  margin-left: ${sizePalette.spacing[2.5]};
  font-size: ${({ theme }) => theme.fontSizePalette.small};
  color: ${({ theme, isEditable }) =>
    theme.colorPalette.isDarkMode
      ? isEditable
        ? theme.colorPalette.grey.grey10
        : theme.colorPalette.grey.grey8
      : theme.colorPalette.grey.grey10};
`;

const FeedbackText = styled.p<{
  feedbackColor: string | undefined;
  displayText?: string;
  textPositioning?: string;
}>`
  font-family: Roboto;
  font-size: ${({ theme }) => theme.fontSizePalette.xxSmall};
  color: ${({ feedbackColor, theme }) => (feedbackColor ? feedbackColor : theme.statusColors.error)} !important;

  margin: ${sizePalette.spacing.px} ${sizePalette.spacing[0]} ${sizePalette.spacing[0]} ${sizePalette.spacing.px};
  position: absolute;
  word-wrap: nowrap;
  ${({ displayText }) => displayText && `display: ${displayText}`};
  ${({ textPositioning }) => textPositioning && `position: ${textPositioning}`};

  text-align: left;
  left: -${sizePalette.spacing.px};
  bottom: -${sizePalette.spacing.pxToRem(13)};
`;

export interface ICheckboxProps {
  name?: string;
  label?: string;
  isActive?: boolean;
  action?: (isActive: boolean, e?: React.MouseEvent<HTMLInputElement, MouseEvent>) => void;
  isEditable?: boolean;
  style?: object;
  statusColor?: string;
  feedbackMessage?: string;
  wrapperStyle?: object;
  id?: string;
}

const Checkbox = (props: ICheckboxProps) => {
  const {
    name,
    label,
    action,
    isActive = false,
    isEditable = true,
    style,
    statusColor,
    feedbackMessage = '',
    wrapperStyle,
    id,
  } = props;
  const [checked, setChecked] = useState(isActive);

  useEffect(() => {
    setChecked(isActive);
  }, [isActive]);
  const { theme } = useTheme();
  const actionEvent = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    action && action(!checked, e);
    setChecked(!checked);
  };

  return (
    <CheckWrapper style={wrapperStyle} data-walkthrough={id}>
      <CheckContainer
        color={statusColor}
        isEditable={isEditable}
        onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
          isEditable ? actionEvent(e) : e.stopPropagation();
        }}
        isChecked={checked}
        disabled={!isEditable}
      >
        <Icon
          style={{
            color: isEditable ? theme.colorPalette.turquoise.normal : theme.colorPalette.grey.grey5,
            opacity: checked ? sizePalette.opacity[100] : sizePalette.opacity[0],
            transition: `opacity ${sizePalette.transitionDuration[150]}`,
          }}
          size={14}
          icon="check"
        />
        <StyledCheckbox
          id={id}
          style={style}
          tabIndex={!isEditable ? -1 : 0}
          readOnly={!isEditable}
          type="checkbox"
          name={name}
          value={name}
          checked={checked}
          disabled={!isEditable}
          onChange={() => null}
        />
      </CheckContainer>
      <CheckLabel isEditable={isEditable} hasLabel={Boolean(label)}>
        {label}
      </CheckLabel>
      {feedbackMessage.length > 0 && statusColor && (
        <FeedbackText feedbackColor={statusColor}>{feedbackMessage}</FeedbackText>
      )}
    </CheckWrapper>
  );
};

export default Checkbox;
